
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { debugLogger } from "@/utils/debugLogger";

// Create a unique session ID for this browser session
const sessionId = uuidv4();

interface TrackQuestionParams {
  question: string;
  normalizedQuestion: string;
  hotelId: string;
  responseTimeMs: number;
  isCached: boolean;
  tokenCount: number;
  model?: string;
  metadata?: Record<string, any>;
}

/**
 * Tracks a question in the question_analytics table
 */
export const trackQuestion = async (params: TrackQuestionParams): Promise<void> => {
  try {
    const {
      question,
      normalizedQuestion,
      hotelId,
      responseTimeMs,
      isCached,
      tokenCount,
      model,
      metadata = {}
    } = params;
    
    debugLogger.log(`⭐ Analytics: Attempting to track question "${question.substring(0, 30)}..." for hotel ${hotelId}`);
    
    // Create the data object to insert
    const insertData = {
      question: question || "Unknown question",
      normalized_question: normalizedQuestion || question || "Unknown question",
      hotel_id: hotelId,
      user_session_id: sessionId,
      response_time_ms: responseTimeMs || 0,
      is_cached: Boolean(isCached),
      token_count: tokenCount || 0,
      model: model || "unknown",
      metadata: metadata || {}
    };
    
    // Check if Supabase client is available before proceeding
    if (!supabase) {
      debugLogger.error('⭐ Analytics: Supabase client is not available');
      return;
    }
    
    // Log what we're trying to insert
    debugLogger.log("⭐ Analytics: Inserting data:", JSON.stringify(insertData, null, 2));
    
    // Attempt the insert with enhanced error handling
    const { data, error } = await supabase
      .from('question_analytics')
      .insert(insertData)
      .select('id');
      
    if (error) {
      // Log the complete error details
      debugLogger.error('⭐ Analytics: Failed to save question:', error);
      debugLogger.error('⭐ Analytics: Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
      });
      
      // Check for common errors
      if (error.code === '42501' || error.message.includes('permission denied')) {
        debugLogger.error('⭐ Analytics: This appears to be a Row Level Security (RLS) policy issue.');
      }
    } else {
      debugLogger.log('⭐ Analytics: Question saved successfully with ID:', data[0]?.id);
    }
  } catch (err) {
    // Don't let analytics errors affect the user experience
    debugLogger.error('⭐ Analytics: Unexpected error:', err);
    if (err instanceof Error) {
      debugLogger.error('⭐ Analytics: Error stack:', err.stack);
    }
  }
};

/**
 * Direct database insert for testing purposes
 */
export const testAnalyticsInsert = async (): Promise<{success: boolean, error?: any}> => {
  try {
    // Basic test data
    const testData = {
      question: "Test question from direct analytics test",
      normalized_question: "test question",
      hotel_id: "697b5c11-874b-4663-9663-923cd51fe368",
      user_session_id: "test-session-" + Date.now(),
      response_time_ms: 100,
      is_cached: false,
      token_count: 50,
      model: "test-model",
      metadata: { source: "direct-test" }
    };
    
    debugLogger.log("⭐ Analytics Test: Inserting test data:", JSON.stringify(testData, null, 2));
    
    const { data, error } = await supabase
      .from('question_analytics')
      .insert(testData)
      .select();
      
    if (error) {
      debugLogger.error("⭐ Analytics Test: Insert failed:", error);
      debugLogger.error("⭐ Analytics Test: Error details:", {
        code: error.code,
        message: error.message,
        details: error.details
      });
      return { success: false, error };
    }
    
    debugLogger.log("⭐ Analytics Test: Insert successful:", data);
    return { success: true };
  } catch (err) {
    debugLogger.error("⭐ Analytics Test: Unexpected error:", err);
    return { success: false, error: err };
  }
};

// Make test function available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).testAnalyticsInsert = testAnalyticsInsert;
}
